import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ActionList} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {
  AssigneesResponse as AssigneesData,
  Assignee,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import styles from './Assignees.module.css'
import sidebarListStyles from './SidebarActionList.module.css'
import {UserSectionSkeleton} from './UserSectionSkeleton'
import {useAssigneesQuery} from './data/route-queries'

export function Assignees() {
  return (
    <ErrorBoundary
      boundaryName="Assignees"
      fallback={<span className={styles.emptyState}>Unable to load assignees</span>}
    >
      <Suspense fallback={<UserSectionSkeleton placeholderRows={1} />}>
        <AssigneesContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function AssigneesContent() {
  const {data: assigneesData} = useAssigneesQuery()

  return <AssigneesComponent assigneesData={assigneesData} />
}

function AssigneesComponent({assigneesData}: {assigneesData: AssigneesData}) {
  const assignees = assigneesData.assignees ?? []

  if (assignees.length === 0) {
    return <span className={styles.emptyState}>No one assigned</span>
  }

  return (
    <ActionList variant="full" className={sidebarListStyles.flush}>
      {assignees.map(assignee => (
        <AssigneeRow key={assignee.id} assignee={assignee} />
      ))}
    </ActionList>
  )
}

function AssigneeRow({assignee}: {assignee: Assignee}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('user', {login: assignee.displayLogin})

  return (
    <ActionList.LinkItem
      href={ownerPath({owner: assignee.displayLogin})}
      className={styles.assigneeItem}
      {...hovercardAttrs}
    >
      <ActionList.LeadingVisual>
        <GitHubAvatar src={assignee.avatarUrl} size={20} />
      </ActionList.LeadingVisual>
      <span className={styles.assigneeName}>{assignee.displayLogin}</span>
    </ActionList.LinkItem>
  )
}
